import {Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {AiChatService} from '../../../_services/ai-chat.service';
import {AiChatPanelService, AiChatRichiestaContesto} from '../../../_services/ai-chat-panel.service';
import {AiChatContestiService} from '../../../_services/ai-chat-contesti.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {
  AiChatContestoProposta, AiChatDone, AiChatErrore, AiChatEvento, AiChatGrafico, AiChatScopo, AiChatStato, scopoDi
} from '../../../_models/ai-chat.model';
import {Organizzazione} from '../../../_models/Organizzazione';
import {
  applicaEvento, attesa, comandiConsultati, daMessaggi, dataUtc, DomandaVista, fallisci, interrompi, nuovaRisposta,
  RispostaVista, testoSemplice, VoceChat
} from '../ai-chat-vista';
import {formattaToken, livelloQuota, oraAzzeramento, percentualeQuota} from '../ai-chat-quota';
import {AiChatLarghezza} from '../ai-chat-larghezza';

export const MAX_DOMANDA = 4000;

export const DOMANDE_ESEMPIO = [
  'Quali ordini sono in ritardo questa settimana?',
  'Spedizioni aperte di un cliente e merce mancante',
  'Non conformità registrate negli ultimi 7 giorni',
  'Stato delle risorse in questo momento'
];

// Feature 164: domanda iniziale delle conversazioni di contesto, precompilata e non inviata.
export function domandaContesto(richiesta: AiChatRichiestaContesto): string {
  if (richiesta.ambito === 'generale')
    return 'Aiutami a migliorare il contesto generale di ArchiCONNECT: proponi correzioni e aggiunte utili a tutte le aziende.';
  return richiesta.migliora
    ? 'Aiutami a migliorare il contesto di questa organizzazione: controlla che sia coerente con la configurazione attuale.'
    : 'Aiutami a scrivere il contesto di questa organizzazione: leggi la configurazione e fammi le domande che servono.';
}

export const ETICHETTE_SCOPO: Record<AiChatScopo, string> = {
  contestoGenerale: 'Contesto generale',
  contestoOrganizzazione: 'Contesto organizzazione'
};

/** Feature 159: pannello laterale della chat AI (US1, US2, US3, US4). Unico nell'app, aperto dal bottone
 * «Assistente» dell'header; la conversazione resta quando lo si chiude e si azzera al cambio organizzazione. */
@Component({
  selector: 'app-ai-chat-panel',
  templateUrl: './ai-chat-panel.component.html',
  styleUrls: ['./ai-chat-panel.component.scss'],
  standalone: false
})
export class AiChatPanelComponent implements OnInit, OnDestroy {
  @ViewChild('messaggi') messaggiRef?: ElementRef<HTMLElement>;
  @ViewChild('campo') campoRef?: ElementRef<HTMLTextAreaElement>;

  vista: 'chat' | 'archivio' = 'chat';
  stato?: AiChatStato;
  erroreStato = '';
  conversazioneId: string | null = null;
  /** Feature 164: scopo della conversazione aperta (null = chat normale). */
  scopo: AiChatScopo | null = null;
  avvisoContesto = '';
  titolo = '';
  totaleChat = 0;
  voci: VoceChat[] = [];
  inCorso: RispostaVista | null = null;
  testo = '';
  caricando = false;
  copiata: RispostaVista | null = null;
  readonly larghezza = new AiChatLarghezza();
  readonly domandeEsempio = DOMANDE_ESEMPIO;
  readonly maxDomanda = MAX_DOMANDA;
  readonly formattaToken = formattaToken;
  readonly consultati = comandiConsultati;
  readonly attesa = attesa;
  orgId = 0;
  private ridimensionando = false;
  private stream?: Subscription;
  private readonly subscriptions = new Subscription();
  readonly panelService = inject(AiChatPanelService);
  private readonly aiChatService = inject(AiChatService);
  private readonly organizzazioniService = inject(OrganizzazioniService);
  private readonly router = inject(Router);
  private readonly contestiService = inject(AiChatContestiService);

  get etichettaScopo(): string {
    return this.scopo ? ETICHETTE_SCOPO[this.scopo] : '';
  }

  get aperto(): boolean {
    return this.panelService.aperto && this.panelService.disponibile;
  }

  get quota(): number {
    return this.stato?.quotaGiornaliera ?? 0;
  }

  get usatiOggi(): number {
    return this.stato?.consumi.oggiOrganizzazione ?? 0;
  }

  get tokenChat(): number {
    return this.totaleChat + (this.inCorso ? this.inCorso.inputTokens + this.inCorso.outputTokens : 0);
  }

  get percentuale(): number {
    return percentualeQuota(this.usatiOggi, this.quota);
  }

  get livello(): string {
    return livelloQuota(this.usatiOggi, this.quota);
  }

  get bloccata(): boolean {
    return !!this.stato?.quotaEsaurita;
  }

  get avvisoQuota(): string {
    if (this.quota <= 0) return 'La chat è disattivata per questa organizzazione: un amministratore può impostare una quota.';
    return `L'organizzazione ha usato ${formattaToken(this.usatiOggi)} token su ${formattaToken(this.quota)}. ` +
      `La chat torna disponibile alle ${this.oraRipresa}; un amministratore può aumentare la quota.`;
  }

  get oraRipresa(): string {
    return this.stato ? oraAzzeramento(dataUtc(this.stato.consumi.azzeramentoUtc)) : '';
  }

  get tooltipConsumi(): string {
    const consumi = this.stato?.consumi;
    return consumi ? `I tuoi consumi: settimana ${formattaToken(consumi.settimanaPersonale)}, ` +
      `mese ${formattaToken(consumi.mesePersonale)} token` : '';
  }

  ngOnInit() {
    this.orgId = this.organizzazioniService.currentOrganizzazioneValue?.id ?? 0;
    this.subscriptions.add(this.panelService.aperto$.subscribe(aperto => aperto && this.suApertura()));
    this.subscriptions.add(this.organizzazioniService.currentOrganizzazione
      .subscribe((org: Organizzazione) => this.suCambioOrganizzazione(org?.id ?? 0)));
    this.subscriptions.add(this.panelService.contesto$.subscribe(richiesta => this.apriContesto(richiesta)));
  }

  ngOnDestroy() {
    this.stream?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  esc() {
    if (this.aperto) this.panelService.chiudi();
  }

  @HostListener('window:resize')
  finestraRidimensionata() {
    this.larghezza.adatta();
  }

  isDomanda(voce: VoceChat): voce is DomandaVista {
    return voce.ruolo === 'user';
  }

  asRisposta(voce: VoceChat): RispostaVista {
    return voce as RispostaVista;
  }

  async caricaStato() {
    if (!this.orgId) return;
    try {
      this.stato = await this.aiChatService.statoAsync(this.orgId);
      this.erroreStato = '';
    } catch (errore) {
      this.erroreStato = (errore as AiChatErrore).messaggio;
    }
  }

  nuovaChat() {
    this.fermaStream();
    Object.assign(this, {conversazioneId: null, titolo: '', totaleChat: 0, voci: [], vista: 'chat', scopo: null,
      avvisoContesto: ''});
    this.metteFuoco();
  }

  /** Feature 164: nuova conversazione di contesto con la domanda iniziale nel campo. Una risposta in corso non
   * viene interrotta: il pannello resta sulla conversazione attuale e lo dice. */
  apriContesto(richiesta: AiChatRichiestaContesto) {
    if (this.inCorso) {
      this.vista = 'chat';
      this.avvisoContesto = 'Attendi la fine della risposta o fermala, poi premi di nuovo «Genera con l\'assistente».';
      return;
    }
    if (richiesta.orgId !== this.orgId) return;
    this.nuovaChat();
    this.scopo = scopoDi(richiesta.ambito);
    this.testo = domandaContesto(richiesta);
  }

  /** «Apri nell'editor»: la proposta passa all'editor del contesto nella scheda «Assistente AI». */
  apriProposta(proposta: AiChatContestoProposta) {
    this.contestiService.impostaProposta(proposta);
    void this.router.navigate(['/organizzazioni/detail', proposta.organizzazioneId], {queryParams: {tab: 'ai'}});
    if (this.larghezza.mobile) this.panelService.chiudi();
  }

  async apriConversazione(id: string) {
    this.fermaStream();
    this.vista = 'chat';
    this.caricando = true;
    try {
      const dettaglio = await this.aiChatService.dettaglioAsync(id);
      Object.assign(this, {conversazioneId: dettaglio.id, titolo: dettaglio.titolo, totaleChat: dettaglio.totaleToken,
        voci: daMessaggi(dettaglio.messaggi), scopo: dettaglio.scopo ?? null, avvisoContesto: ''});
      this.scorriInFondo(true);
    } catch (errore) {
      this.erroreStato = (errore as AiChatErrore).messaggio;
    } finally {
      this.caricando = false;
    }
  }

  conversazioneEliminata(id: string) {
    if (id === this.conversazioneId) this.nuovaChat();
  }

  tastiera(evento: KeyboardEvent) {
    if (evento.key !== 'Enter' || evento.shiftKey || evento.isComposing) return;
    evento.preventDefault();
    void this.invia();
  }

  puoInviare(domanda: string): boolean {
    return domanda.length > 0 && domanda.length <= MAX_DOMANDA && !this.inCorso && !this.bloccata && this.orgId > 0;
  }

  async invia(testo = this.testo) {
    const domanda = testo.trim();
    if (!this.puoInviare(domanda)) return;
    this.testo = '';
    this.avvisoContesto = '';
    const risposta = this.aggiungiScambio(domanda);
    try {
      this.ascolta(await this.assicuraConversazione(), domanda, risposta);
    } catch (errore) {
      this.erroreRisposta(risposta, errore as AiChatErrore);
    }
  }

  stop() {
    if (!this.inCorso) return;
    this.stream?.unsubscribe();
    interrompi(this.inCorso);
    this.inCorso = null;
    this.metteFuoco();
  }

  async copia(risposta: RispostaVista) {
    try {
      await navigator.clipboard.writeText(testoSemplice(risposta.testo));
      this.copiata = risposta;
      setTimeout(() => this.copiata = this.copiata === risposta ? null : this.copiata, 1500);
    } catch {
      this.copiata = null;
    }
  }

  /** Feature 160: i link alle entità navigano col router; Ctrl/⌘/Maiusc/clic centrale restano al browser.
   * Su mobile il pannello copre la pagina, quindi si chiude. */
  apriEntita(evento: MouseEvent) {
    const link = (evento.target as HTMLElement | null)?.closest?.('a.ai-chat__entita') as HTMLAnchorElement | null;
    if (!link || evento.button !== 0 || evento.ctrlKey || evento.metaKey || evento.shiftKey || evento.altKey) return;
    evento.preventDefault();
    this.router.navigateByUrl(link.getAttribute('href') ?? '/');
    if (this.larghezza.mobile) this.panelService.chiudi();
  }

  idGrafico(_: number, grafico: AiChatGrafico): string {
    return grafico.id;
  }

  iniziaRidimensionamento(evento: PointerEvent) {
    (evento.target as HTMLElement).setPointerCapture?.(evento.pointerId);
    this.ridimensionando = true;
    evento.preventDefault();
  }

  ridimensiona(evento: PointerEvent) {
    if (this.ridimensionando) this.larghezza.trascina(evento.clientX);
  }

  fineRidimensionamento() {
    if (!this.ridimensionando) return;
    this.ridimensionando = false;
    this.larghezza.salva();
  }

  private suApertura() {
    void this.caricaStato();
    this.metteFuoco();
  }

  // La conversazione appartiene all'organizzazione con cui è nata: il server rifiuta le altre (400).
  private suCambioOrganizzazione(orgId: number) {
    if (orgId === this.orgId) return;
    this.orgId = orgId;
    this.stato = undefined;
    this.nuovaChat();
    if (this.panelService.aperto) void this.caricaStato();
  }

  private aggiungiScambio(domanda: string): RispostaVista {
    const risposta = nuovaRisposta();
    this.voci = [...this.voci, {ruolo: 'user', testo: domanda, createdAt: new Date()}, risposta];
    this.inCorso = risposta;
    this.scorriInFondo(true);
    return risposta;
  }

  private async assicuraConversazione(): Promise<string> {
    return this.conversazioneId ?? (await this.aiChatService.creaAsync(this.orgId, this.scopo)).id;
  }

  // Stop o «Nuova chat» durante la creazione della conversazione: non si invia nulla.
  private ascolta(conversazioneId: string, domanda: string, risposta: RispostaVista) {
    if (this.inCorso !== risposta) return;
    this.conversazioneId = conversazioneId;
    this.stream = this.aiChatService.invia(conversazioneId, this.orgId, domanda).subscribe({
      next: evento => this.suEvento(risposta, evento),
      error: (errore: AiChatErrore) => this.erroreRisposta(risposta, errore),
      complete: () => this.fineStream(risposta)
    });
  }

  private suEvento(risposta: RispostaVista, evento: AiChatEvento) {
    applicaEvento(risposta, evento);
    if (evento.type === 'done') this.suDone(evento);
    this.scorriInFondo(false);
  }

  private suDone(done: AiChatDone) {
    this.titolo = done.titolo;
    this.totaleChat = done.totaleChat;
    if (this.stato) {
      this.stato.consumi.oggiOrganizzazione = done.oggiOrganizzazione;
      this.stato.quotaEsaurita = done.quotaEsaurita;
    }
  }

  // Stream chiuso senza `done` né `error` (rete caduta, proxy): la risposta resta incompleta.
  private fineStream(risposta: RispostaVista) {
    if (risposta.fase !== 'conclusa') fallisci(risposta, 'La risposta si è interrotta: riprova.');
    if (this.inCorso === risposta) this.inCorso = null;
  }

  private erroreRisposta(risposta: RispostaVista, errore: AiChatErrore) {
    fallisci(risposta, errore.messaggio);
    if (this.inCorso === risposta) this.inCorso = null;
    if (errore.codice === 'QUOTA_ESAURITA') void this.caricaStato();
    if (errore.status === 404) this.conversazioneId = null;
  }

  private fermaStream() {
    this.stop();
    this.stream?.unsubscribe();
  }

  private metteFuoco() {
    setTimeout(() => this.campoRef?.nativeElement.focus());
  }

  // Segue la risposta solo se l'operatore è già in fondo: non lo strappa da ciò che sta rileggendo.
  private scorriInFondo(sempre: boolean) {
    const elemento = this.messaggiRef?.nativeElement;
    const inFondo = !elemento || elemento.scrollHeight - elemento.scrollTop - elemento.clientHeight < 80;
    if (sempre || inFondo) setTimeout(() => this.messaggiRef?.nativeElement.scrollTo({top: 1e9}));
  }
}
